import { Link } from "react-router";
import type { Occurrence } from "@talentkontor/domain";
import { Dialog, KachelBetrag, Money } from "../../../components/ui";
import { money, monthLabel } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import { istHerkunft } from "../../kommunikation/kennzahlen/model";

/** Monatsdetails (bisheriges Portal AD-023): Summen und Einzelposten eines Monats unter dem aktiven Filter. */
export function MonatsDetails({ month, occurrences, currency, onClose, dealTitle, talentName, brandName, dealModel }: {
  month: string | null;
  occurrences: Occurrence[];
  currency: string;
  onClose: () => void;
  dealTitle: (id: string) => string;
  talentName: (id: string) => string;
  brandName: (id: string) => string;
  /** Vergütungsmodell der Kooperation (Beschriftung des Ist bei „Nur Provision“) */
  dealModel?: (id: string) => string | null;
}) {
  const list = month ? occurrences.filter((o) => o.month === month) : [];
  const sum = (f: (o: Occurrence) => number) => list.reduce((a, o) => a + f(o), 0);
  const vermittler = sum((o) => o.referralCents);
  return (
    <Dialog open={!!month} onClose={onClose} title={month ? t(`${monthLabel(month)} · Details`, `${monthLabel(month)} · details`) : ""} wide>
      <div className="tk-grid-3">
        <div className="kpi"><small>{t("Agentur-Einnahmen (Provision + Gebühr)", "Agency income (commission + fee)")}</small><KachelBetrag cents={sum((o) => o.managementCents)} currency={currency} />
          <small>{t("netto", "net")}{vermittler ? t(` · nach Vermittlerprovision ${money(sum((o) => o.managementCents) - vermittler, currency)}`, ` · after referral commission ${money(sum((o) => o.managementCents) - vermittler, currency)}`) : ""}</small></div>
        <div className="kpi"><small>{t("Gesamtvolumen · netto", "Total volume · net")}</small><KachelBetrag cents={sum((o) => o.grossCents)} currency={currency} /><small>{t("Budget, Zusatz und Gebühr vor Aufteilung, ohne Umsatzsteuer", "Budget, extras and fee before split, excluding VAT")}</small></div>
        <div className="kpi"><small>{t("Posten", "Items")}</small><div className="metric num">{list.length}</div><small>{list.filter((o) => o.booked).length} {t("verbucht · ", "booked · ")}{list.filter((o) => !o.booked).length} {t("vorläufig", "provisional")}</small></div>
      </div>
      <div className="tablewrap">
        <table>
          <caption className="sr-only">{t("Einzelposten", "Line items")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("Kooperation", "Deal")}</th><th scope="col">Creator</th><th scope="col">{t("Marke", "Brand")}</th>
              <th scope="col" className="money">Budget</th><th scope="col" className="money">{t("Provision", "Commission")}</th><th scope="col" className="money">{t("Gebühr", "Fee")}</th>
              {vermittler > 0 && <th scope="col" className="money">{t("Vermittler", "Referrers")}</th>}<th scope="col">{t("Stand", "Status")}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((o) => (
              <tr key={o.periodId}>
                <td>{o.source === "deal" ? <Link to={`/agentur/kooperationen/${o.dealId}?monat=${o.month}`}>{dealTitle(o.dealId)}</Link> : <Link to={`/agentur/kennzahlen?bmonat=${o.month}`}>{t("Manuelle Buchung", "Manual booking")}</Link>}</td>
                <td>{o.talentId ? talentName(o.talentId) : "–"}</td>
                <td>{o.brandId ? brandName(o.brandId) : "–"}</td>
                <td><Money cents={o.budgetCents + o.extraCents} currency={currency} /></td>
                <td><Money cents={o.agencyShareCents} currency={currency} /></td>
                <td><Money cents={o.feeCents} currency={currency} /></td>
                {vermittler > 0 && <td>{o.referralCents ? <Money cents={-o.referralCents} currency={currency} /> : <span className="muted">–</span>}</td>}
                <td>
                  <span className={`pill ${o.booked ? "success" : "info"}`}><span className="pill-zeichen">{o.booked ? "✓" : "◇"}</span> {o.booked ? t("Verbucht", "Booked") : t("Vorläufig", "Provisional")}</span>
                  {/* Herkunft des Ist je Weg: Creator-Rechnung, Provisions- oder Agenturrechnung (GELD-26) */}
                  {o.source === "deal" && istHerkunft(o, dealModel?.(o.dealId)) && <small>{istHerkunft(o, dealModel?.(o.dealId))}</small>}
                  {o.cancellationFee && <small>{t("Ausfallhonorar", "Cancellation fee")}</small>}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={vermittler > 0 ? 8 : 7}>{t("In diesem Monat gibt es keine Posten.", "There are no items in this month.")}</td></tr>}
          </tbody>
        </table>
      </div>
      {month && (
        <div className="row">
          <Link className="btn" to={`/agentur/kennzahlen?bmonat=${month}&neu=1`}>{t("Buchung in diesem Monat anlegen", "Create booking in this month")}</Link>
          <Link className="btn" to={`/agentur/kennzahlen?bmonat=${month}`}>{t("Buchungen des Monats öffnen", "Open the month's bookings")}</Link>
        </div>
      )}
    </Dialog>
  );
}
